// Default duration for slide animations in milliseconds
const DEFAULT_ANIMATION_DURATION_MS = 500;

/**
 * Slide up and hide an element with a smooth animation.
 */
export const slideUp = (
  element: HTMLElement,
  duration = DEFAULT_ANIMATION_DURATION_MS,
): Promise<void> => {
  return new Promise((resolve) => {
    element.style.overflow = 'hidden';

    const startHeight = `${element.offsetHeight}px`;

    const animation = element.animate(
      [
        {
          height: startHeight,
          paddingTop: getComputedStyle(element).paddingTop,
          paddingBottom: getComputedStyle(element).paddingBottom,
          marginTop: getComputedStyle(element).marginTop,
          marginBottom: getComputedStyle(element).marginBottom,
          opacity: 1,
        },
        {
          height: '0px',
          paddingTop: '0px',
          paddingBottom: '0px',
          marginTop: '0px',
          marginBottom: '0px',
          opacity: 0,
        },
      ],
      { duration, easing: 'ease' },
    );

    animation.onfinish = () => {
      element.style.display = 'none';
      element.style.removeProperty('overflow');
      resolve();
    };
  });
};

/**
 * Slide down and reveal an element with a smooth animation.
 */
export const slideDown = (
  element: HTMLElement,
  duration = DEFAULT_ANIMATION_DURATION_MS,
): Promise<void> => {
  return new Promise((resolve) => {
    element.style.removeProperty('display');
    let display = getComputedStyle(element).display;
    if (display === 'none') display = 'block';
    element.style.display = display;

    const endHeight = `${element.offsetHeight}px`;
    const paddingTop = getComputedStyle(element).paddingTop;
    const paddingBottom = getComputedStyle(element).paddingBottom;
    const marginTop = getComputedStyle(element).marginTop;
    const marginBottom = getComputedStyle(element).marginBottom;

    element.style.overflow = 'hidden';

    const animation = element.animate(
      [
        {
          height: '0px',
          paddingTop: '0px',
          paddingBottom: '0px',
          marginTop: '0px',
          marginBottom: '0px',
          opacity: 0,
        },
        { height: endHeight, paddingTop, paddingBottom, marginTop, marginBottom, opacity: 1 },
      ],
      { duration, easing: 'ease' },
    );

    animation.onfinish = () => {
      element.style.removeProperty('height');
      element.style.removeProperty('padding-top');
      element.style.removeProperty('padding-bottom');
      element.style.removeProperty('margin-top');
      element.style.removeProperty('margin-bottom');
      element.style.removeProperty('overflow');
      resolve();
    };
  });
};

/**
 * Toggle the slide state of an element with a smooth animation.
 */
export const slideToggle = (
  element: HTMLElement,
  duration = DEFAULT_ANIMATION_DURATION_MS,
): Promise<void> => {
  const isHidden = getComputedStyle(element).display === 'none';

  return isHidden ? slideDown(element, duration) : slideUp(element, duration);
};
